/**
 * Where to send someone after they sign in.
 *
 * THE BUG THIS EXISTS TO FIX. A guard that bounces you to the login page
 * records where you were trying to go, and the login page sends you back there
 * afterwards. That is right until the place you were trying to go is one your
 * role cannot reach — then it bounces you to login again, and again, forever.
 *
 * It is reachable from the signed-out home page in two clicks: the "Upload
 * Customer Master" tile links to #/upload/cm, which is admin-only. A brand user
 * clicks it, gets sent to login, signs in correctly, and lands back on login
 * with no explanation. Nothing is broken server-side and no error appears
 * anywhere; the credentials were fine.
 *
 * THE FIX IS NOT "REFUSE TO REDIRECT". Dropping the returnTo would stop the
 * loop but strand the user somewhere they did not ask to be, having just told
 * them the page existed. Most admin pages a brand user hits this way have a
 * brand-scoped equivalent that does exactly what they wanted — #/upload/cm is
 * the admin Customer Master upload, and #/brand/<slug>/upload/cm is theirs. So
 * the redirect is TRANSLATED rather than dropped, and only falls back to the
 * brand home when there is genuinely no equivalent.
 */
import { ADMIN_ONLY_PAGES, parseRoute } from "./route";

/**
 * Admin page -> the brand-scoped page that does the same job.
 *
 * Only pages with a real equivalent belong here. Everything else in
 * ADMIN_ONLY_PAGES (tenant admin, signups, the GBP ambiguity queue) is
 * genuinely admin-only work with no brand counterpart, and falls through to
 * the brand's home rather than pretending otherwise.
 */
const BRAND_EQUIVALENT: Record<string, (slug: string) => string> = {
  upload: (s) => `#/brand/${encodeURIComponent(s)}/onboard`,
  "upload-cm": (s) => `#/brand/${encodeURIComponent(s)}/upload/cm`,
  "upload-wtb": (s) => `#/brand/${encodeURIComponent(s)}/upload/wtb`,
  // The admin review queue's brand counterpart is the Accounts workspace,
  // which is where a brand confirms its own matches.
  review: (s) => `#/brand/${encodeURIComponent(s)}/accounts`,
  "admin-mappings-adspend": (s) => `#/brand/${encodeURIComponent(s)}/accounts`,
};

/** Where a brand user lands when nothing more specific applies. */
export function brandHome(slug: string): string {
  return `#/brand/${encodeURIComponent(slug)}/onboard`;
}

export interface PostLoginInput {
  /** The `?redirect=` value the guard recorded, if any. */
  returnTo: string | null;
  /** The role the server just authenticated. */
  role: string;
  /** The tenant slug the server just authenticated. */
  slug: string;
}

export interface PostLoginTarget {
  hash: string;
  /**
   * Set when the request was translated or dropped, so the UI can say why the
   * user did not land where they clicked. Silence here would be its own small
   * mystery.
   */
  note?: string;
}

/**
 * Resolve the hash to navigate to after a successful sign-in.
 *
 * Pure, so the loop it prevents can be tested without a browser.
 */
export function resolvePostLoginTarget({ returnTo, role, slug }: PostLoginInput): PostLoginTarget {
  const isAdmin = role === "admin";
  const fallback = isAdmin ? "#/upload" : brandHome(slug);

  if (!returnTo) return { hash: fallback };

  // Only same-app hash routes. An absolute URL or a protocol-relative one in
  // this parameter is an open-redirect attempt, not a destination.
  if (!returnTo.startsWith("#/") || returnTo.startsWith("#//")) {
    return { hash: fallback };
  }

  const page = parseRoute(returnTo).page;

  // Bouncing back to login is the degenerate case of the same loop.
  if (page === "login") return { hash: fallback };

  // An admin may go wherever they were headed.
  if (isAdmin || !ADMIN_ONLY_PAGES.has(page)) return { hash: returnTo };

  // Non-admin, admin-only destination: translate rather than loop.
  const equivalent = BRAND_EQUIVALENT[page];
  if (equivalent) {
    return {
      hash: equivalent(slug),
      note: "That link goes to the admin version of this page. We've taken you to your brand's version instead.",
    };
  }
  return {
    hash: brandHome(slug),
    note: "That page is only available to Heady admins, so we've taken you to your brand's home instead.",
  };
}
